import ApiService from './api';
import { User, LoginRequest, RegisterRequest } from '@/types';

// 后端 Token 响应格式
interface BackendTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_at: string;
  user: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    role: string;
    phone?: string;
    two_factor_enabled?: boolean;
    merchant_ids?: string[];
    current_merchant_id?: string;
  };
}

export interface LoginResponse {
  user: User;
  token: string;
  expires_in: number;
}

export interface RegisterResponse {
  user: User;
  token: string;
  message: string;
}

export class AuthService {
  // 用户登录
  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await ApiService.post<BackendTokenResponse>('/auth/login', credentials);

    // 保存认证信息到本地存储
    if (response.success && response.data) {
      const backendData = response.data;
      localStorage.setItem('token', backendData.access_token);
      localStorage.setItem('refresh_token', backendData.refresh_token);
      localStorage.setItem('user', JSON.stringify(backendData.user));

      // 转换为前端期望的格式
      return {
        user: backendData.user as unknown as User,
        token: backendData.access_token,
        expires_in: 3600, // 默认1小时
      };
    }

    return response.data as unknown as LoginResponse;
  }

  // 用户注册
  static async register(userData: RegisterRequest): Promise<RegisterResponse> {
    const response = await ApiService.post<BackendTokenResponse>('/auth/register', userData);

    // 保存认证信息到本地存储
    if (response.success && response.data) {
      const backendData = response.data;
      localStorage.setItem('token', backendData.access_token);
      localStorage.setItem('refresh_token', backendData.refresh_token);
      localStorage.setItem('user', JSON.stringify(backendData.user));

      // 转换为前端期望的格式
      return {
        user: backendData.user as unknown as User,
        token: backendData.access_token,
        message: 'Registration successful',
      };
    }

    return response.data as unknown as RegisterResponse;
  }

  // 刷新Token
  static async refreshToken(): Promise<LoginResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    const response = await ApiService.post<BackendTokenResponse>('/auth/refresh-token', {
      refresh_token: refreshToken,
    });

    if (response.success && response.data) {
      const backendData = response.data;
      localStorage.setItem('token', backendData.access_token);
      localStorage.setItem('refresh_token', backendData.refresh_token);
      localStorage.setItem('user', JSON.stringify(backendData.user));

      // 转换为前端期望的格式
      return {
        user: backendData.user as unknown as User,
        token: backendData.access_token,
        expires_in: 3600,
      };
    }

    return response.data as unknown as LoginResponse;
  }

  // 用户登出
  // Note: Backend doesn't have /auth/logout endpoint - logout is client-side only
  static async logout(): Promise<void> {
    try {
      // Backend doesn't have logout endpoint - just clear local storage
      // await ApiService.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // 清除本地存储
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('api_key');
    }
  }

  // 忘记密码
  static async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await ApiService.post<{ message: string }>('/auth/forgot-password', {
      email,
    });
    return response.data;
  }

  // 重置密码
  static async resetPassword(
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<{ message: string }> {
    const response = await ApiService.post<{ message: string }>('/auth/reset-password', {
      token,
      password,
      confirm_password: confirmPassword,
    });
    return response.data;
  }

  // 邮箱验证
  static async verifyEmail(token: string): Promise<{ message: string }> {
    const response = await ApiService.post<{ message: string }>('/auth/verify-email', {
      token,
    });
    return response.data;
  }

  // 获取当前用户信息
  static async getCurrentUser(): Promise<User> {
    const response = await ApiService.get<User>('/merchant/profile');
    return response.data;
  }

  // 更新用户信息
  static async updateProfile(userData: Partial<User>): Promise<User> {
    const response = await ApiService.put<User>('/merchant/profile', userData);
    
    // 更新本地存储的用户信息
    if (response.success && response.data) {
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    
    return response.data;
  }

  // Note: Backend doesn't have /auth/change-password endpoint
  // Password changes should be done through settings or password reset flow
  // static async changePassword(
  //   currentPassword: string,
  //   newPassword: string
  // ): Promise<{ message: string }> {
  //   const response = await ApiService.post<{ message: string }>('/auth/change-password', {
  //     current_password: currentPassword,
  //     new_password: newPassword,
  //   });
  //   return response.data;
  // }

  // 启用两步验证
  static async enableTwoFactor(): Promise<{
    qr_code: string;
    secret: string;
    backup_codes: string[];
  }> {
    const response = await ApiService.post<{
      qr_code: string;
      secret: string;
      backup_codes: string[];
    }>('/merchant/settings/security/two-factor/enable');
    return response.data;
  }

  // 禁用两步验证
  static async disableTwoFactor(code: string): Promise<{ message: string }> {
    const response = await ApiService.post<{ message: string }>(
      '/merchant/settings/security/two-factor/disable',
      { code }
    );
    return response.data;
  }

  // Note: Backend doesn't have /auth/verify-two-factor endpoint
  // Two-factor verification should be handled during login flow
  // static async verifyTwoFactor(code: string): Promise<{ valid: boolean }> {
  //   const response = await ApiService.post<{ valid: boolean }>('/auth/verify-two-factor', {
  //     code,
  //   });
  //   return response.data;
  // }

  // OAuth登录
  static getOAuthUrl(provider: string): string {
    return `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1'}/auth/oauth/${provider}`;
  }

  // 检查认证状态
  static isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    return !!(token && user);
  }

  // 获取本地存储的用户信息
  static getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Parse user error:', error);
        return null;
      }
    }
    return null;
  }

  // 获取本地存储的token
  static getStoredToken(): string | null {
    return localStorage.getItem('token');
  }

  // 检查token是否过期
  static isTokenExpired(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  }
}

export default AuthService; 